'use client'

import { useCallback } from 'react'
import { SideDrawer } from '@/components/ui/Drawer'
import Preview from '@/components/Preview'
import { Button } from '@/components/ui/Button'
import type { HeroDemo } from '@kiltechco/dday-templates/demos'

interface DesignPreviewContainerProps {
  isOpen: boolean
  onClose: () => void
  design: HeroDemo | null
  onSelect: (design: HeroDemo) => void
}

export default function DesignPreviewContainer({
  isOpen,
  onClose,
  design,
  onSelect,
}: DesignPreviewContainerProps) {
  const handleSelect = useCallback(() => {
    if (!design) return
    onClose()
    onSelect(design)
  }, [design, onClose, onSelect])

  // noopener/noreferrer so COOP does not block postMessage between opener and popup.
  const handleFullPagePreview = useCallback(() => {
    if (!design) return
    window.open(`/designs/demo/${design.id}`, '_blank', 'noopener,noreferrer')
  }, [design])

  if (!design) return null

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Design Demo"
      contentLayout="sticky-footer"
    >
      {isOpen && (
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-smooth overscroll-contain px-2 scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="mt-4 mb-4 space-y-3 text-center">
              <h3 className="text-xl font-semibold text-foreground">
                {design.label}
              </h3>
              <p className="text-sm text-muted-foreground">
                {design.description}
              </p>
            </div>

            <div className="flex justify-center pb-4">
              <Preview
                templateData={design.data}
                onFullPagePreview={handleFullPagePreview}
              />
            </div>
          </div>
          <div className="shrink-0 p-4 border-t border-border bg-card">
            <Button
              onClick={handleSelect}
              className="w-full touch-manipulation"
              size="lg"
            >
              Use This Design
            </Button>
          </div>
        </div>
      )}
    </SideDrawer>
  )
}
